import { of } from 'rxjs'
import { mergeMap, catchError } from 'rxjs/operators'
import { continueWatchingDataFactory } from '../factories'
import UserPreferencesApi from '../UserPreferencesApi'
import Fetch from '../fetch'
import DataHelper from 'framework/helpers/data'
import Constants from 'api-constants'
import ConfigHelper from 'framework/helpers/config'
import {
    getNagraDeviceType,
    getNagraContentType,
    getNagraRecommendationType,
    getVodsFilteredByType,
} from '../helpers'
import { NagraTypes } from '../models/NagraTypes'
import { getEmiRequestMapForLive, getEmiRequestMapForVod } from './helpers'

export default class ProgramApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.userPreferencesApi = otherApis.userPreferencesApi
        this.proxyApi = otherApis.proxyApi
    }
    /**
     * Get programs informations (stream data, product data) from the given
     * list of `titleIds`
     *
     * @param {Array<String>} titleIds Title identifiers to retrieve
     * @return {Observable<Object.<string, ProgramData>>} Programs data, keyed by program `titleId`
     */
    getPrograms(titleIds) {
        return of({})
    }

    /**
     * *Note:* Content PlatformId can be retrieve only if user is allowed to watch content.
     *
     * @param {String} streamId
     * @param {Boolean} [isRecording=false]
     * @param {String} [userAgent]
     * @return {ProgramPlatformData|null} Programs platform data or null
     */
    getProgramPlatformId(streamId, isRecording = false, userAgent) {
        const data = {
            playBackPlatformId: streamId,
            duration: null,
            channel: null,
        }
        return of(data)
    }

    /**
     * Create or update series bookmark
     * @param {String} titleId id of the episode
     * @param {Number} bookmark watch position of ongoing episode
     * @param {String} serieID associated serie's id
     * @returns {observable<>}
     */
    updateSerieBookmark(titleId, bookmark, serieID) {
        // if series already has bookmarks no need to delete it. Nagra will automatically update fields
        return this.userPreferencesApi.addBookmark(
            bookmark,
            serieID,
            UserPreferencesApi.LEGACY_BOOKMARK_SERIES_TAG,
            { expiresDate: Date.now(), lastPlayedEpisode: titleId },
            [UserPreferencesApi.LEGACY_BOOKMARK_SERIES_TAG]
        )
    }

    /**
     * Set a bookmark for a specific Title identifier.
     *
     * **Important**
     * For backward compatibility this method also handle `setBookmark(titleId, bookmark)` signature.
     * This usage is deprecated and will be removed later.
     *
     * @param {Object} options
     * @param {String} options.titleId Title platform identifier
     * @param {Number} options.bookmark In seconds
     * @param {String} options.programType one of `Constants.programType.*`
     *
     * @returns {Observable<Boolean>} Observable which emit a boolean, true = bookmark supported, false = bookmark not supported
     */
    setBookmark({ titleId, bookmark, programType, serieID }) {
        if (
            programType !== Constants.programType.vod &&
            programType !== Constants.programType.dvr
        ) {
            this.logger.info('Bookmarks are only available for VOD or DVR programs Nagra')
            return of(false)
        }
        if (this.config.useNagraLegacyData) {
            if (serieID) {
                return this.updateSerieBookmark(titleId, bookmark, serieID).pipe(
                    mergeMap(() => {
                        return this.userPreferencesApi.addBookmark(
                            bookmark,
                            titleId,
                            UserPreferencesApi.LEGACY_BOOKMARK_TAG,
                            {},
                            [UserPreferencesApi.LEGACY_BOOKMARK_TAG]
                        )
                    })
                )
            } else {
                return this.userPreferencesApi.addBookmark(
                    bookmark,
                    titleId,
                    UserPreferencesApi.LEGACY_BOOKMARK_TAG,
                    {},
                    [UserPreferencesApi.LEGACY_BOOKMARK_TAG]
                )
            }
        }
        return this.userPreferencesApi.addBookmark(bookmark, titleId, programType)
    }

    /**
     * Retrieves meta data of Live Series
     *
     * @param {Array<String>} seriesIds The series' identifiers
     * @returns {Observable<Array<LiveTvShow>>} A list of instances of {@link LiveTvShow}
     */
    getLiveSeriesData(seriesIds) {
        return of([])
    }

    /**
     * Determines the list of {@link EmiRequest} that can be used for fetching related content for different data types
     * @param {Program} program The {@link Program} for which related content is wanted
     * @param {Object} args
     * @returns {Observable<Object<String,EmiRequest>>} A map assigning an {@link EmiRequest} for each supported data type of related content
     */
    getRelatedRequests(program) {
        if (!program) return of({})

        if (program.isVod && program.isVod?.()) {
            return getEmiRequestMapForVod(program, this.config.platform)
        }
        if (program.isLive && program.isLive()) return getEmiRequestMapForLive(program)
        return of({})
    }

    /**
     * @returns {Observable<ProgramListResponse>}
     */
    getRelated({
        id,
        programType,
        relatedType,
        program,
        page = 0,
        limit = 15,
        optaDetails: { gameIds = [], sportType = 'soccer' } = {},
    }) {
        const nagraContentType = getNagraContentType(programType)
        let episodeId
        if (program?.isTvShow?.()) {
            episodeId = program.episodesList?.[0]?.episodeId + '_VERSION_DASH'
        }
        if (!nagraContentType) {
            this.logger.warn(
                'Related: Unsupported content type. Do not trigger item to item reco backend call...'
            )
            return of({ programs: [], done: true })
        }

        const recommendationType = getNagraRecommendationType(relatedType)
        if (recommendationType === NagraTypes.RECOMMENDATION_TYPE.UNKNOWN) {
            this.logger.warn(
                'Related: Unsupported recommendation type. Do not trigger item to item reco backend call...'
            )
            return of({ programs: [], done: true })
        }

        const isVod =
            recommendationType === NagraTypes.RECOMMENDATION_TYPE.VOD ||
            recommendationType === NagraTypes.RECOMMENDATION_TYPE.EPL
        let basedOnContent = []
        switch (nagraContentType) {
            case NagraTypes.CONTENT_TYPE.BTV:
                basedOnContent = [
                    {
                        contentType: nagraContentType,
                        btvProgrammeId: id,
                    },
                ]
                break
            case NagraTypes.CONTENT_TYPE.VOD:
                basedOnContent = [
                    {
                        contentType: nagraContentType,
                        vodTechnicalId: program?.isTvShow?.() ? episodeId : id,
                    },
                ]
                break
            case NagraTypes.CONTENT_TYPE.SERIES:
                basedOnContent = [
                    {
                        contentType: nagraContentType,
                        seriesId: id,
                    },
                ]
                break
            case NagraTypes.CONTENT_TYPE.SEASON:
                basedOnContent = [
                    {
                        contentType: nagraContentType,
                        seasonId: id,
                    },
                ]
                break

            case NagraTypes.CONTENT_TYPE.DEEPLINK:
                basedOnContent = [
                    {
                        contentType: nagraContentType,
                        deeplinkId: id,
                    },
                ]
                break
            default:
                break
        }

        return this.fetch({
            url: `${
                this.config.urls.apiUrl
            }/recommendations/v1/recommendations?token=${DataHelper.getInstance().getSecondaryAccessToken()}`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                tenantId: ConfigHelper.getInstance().getConfig('nagra').tenantId,
            },
            body: {
                recommendationRequests: [
                    {
                        recommendationType,
                        deviceTypes: [getNagraDeviceType(this.config.platform)],
                        basedOnContent,
                        numOfRecommendationsPerRow: limit,
                        // Hardcoded language for now. Soon it will be available to set chinese
                        language: 'en',
                    },
                ],
            },
        }).pipe(
            mergeMap(({ response } = {}) => {
                if (
                    !response.recommendations.length &&
                    !response.recommendations[0].recommendationRows.length &&
                    !response.recommendations[0].recommendationRows[0].content.length
                ) {
                    return of({ programs: [], done: true })
                }

                const programs = response.recommendations[0].recommendationRows[0].content
                const programIds = programs
                    .map((program) =>
                        isVod
                            ? program.editorialId || program.seriesId || program.seasonId
                            : program.btvProgrammeId
                    )
                    .filter(Boolean)
                return this.proxyApi.getPrograms({ ids: programIds, isVod })
            }),
            mergeMap((relatedFromProxy) => {
                const programs =
                    isVod && !program?.isTvShow?.()
                        ? getVodsFilteredByType(relatedFromProxy)
                        : relatedFromProxy
                return of({ programs, done: true })
            }),
            catchError((error) => {
                this.logger.warn(error, 'cannot find any related content')
                return of(false)
            })
        )
    }

    /**
     * @returns {Observable<ProgramListResponse>}
     */
    getTrending() {
        return of({ programs: [], done: true })
    }

    /**
     * @returns {Observable<ProgramListResponse>}
     */
    getRecommended() {
        return of({ programs: [], done: true })
    }

    /**
     * Returns data for continue watching
     * @return {Observable<ContinueWatchingData>} continue watching data
     */
    getContinueWatching() {
        return this.userPreferencesApi
            .getBookmarks(
                this.config.useNagraLegacyData
                    ? UserPreferencesApi.LEGACY_BOOKMARK_TAG
                    : UserPreferencesApi.BOOKMARK_TAG
            )
            .pipe(mergeMap((bookmarks = []) => of(continueWatchingDataFactory(bookmarks))))
    }

    /**
     * Not implemented for nagra
     * @returns {Observable}
     */
    getDetails() {
        return of(false)
    }
}

/**
 * @typedef {Object} ContinueWatchingData
 * @property {Array} all
 * @property {Array} vod
 * @property {Object.<string, Number>} programs
 */
